/**
 * AI-powered Firebase Functions integration
 * Provides callable function wrappers for AI features
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './config';

/**
 * Handle common Firebase function errors
 * @param {Error} error - The error from Firebase
 * @param {string} context - Context for error message
 */
const handleFunctionError = (error, context = 'AI service') => {
  console.error(`Error calling ${context}:`, error);

  if (error.code === 'functions/unauthenticated') {
    throw new Error('Please sign in to use AI suggestions');
  }
  if (error.code === 'functions/resource-exhausted') {
    throw new Error('AI service is busy. Please try again in a moment.');
  }
  if (error.code === 'functions/invalid-argument') {
    throw new Error('Invalid input provided');
  }
  if (error.code === 'functions/failed-precondition') {
    throw new Error('AI service is not configured. Please contact support.');
  }

  throw new Error('Failed to generate suggestions. Please try again.');
};

/**
 * Suggest search terms based on organization name using AI
 * @param {string} organizationName - The name of the organization to analyze
 * @returns {Promise<Object>} - Structured suggestions with industry inference and search terms
 */
export const suggestSearchTermsAI = async (organizationName) => {
  const suggestSearchTerms = httpsCallable(functions, 'suggestSearchTerms');

  try {
    const result = await suggestSearchTerms({ organizationName });
    return result.data;
  } catch (error) {
    handleFunctionError(error, 'suggestSearchTerms');
  }
};

/**
 * Suggest competitor domains based on organization name and industry using AI
 * @param {string} organizationName - The name of the organization
 * @param {string} industry - The industry/sector of the organization
 * @param {string} domain - The organization's domain (optional, helps with context)
 * @returns {Promise<Object>} - Suggested competitors with domains and reasoning
 */
export const suggestCompetitorsAI = async (organizationName, industry, domain = '') => {
  const suggestCompetitors = httpsCallable(functions, 'suggestCompetitors');

  try {
    const result = await suggestCompetitors({ organizationName, industry, domain });
    return result.data;
  } catch (error) {
    handleFunctionError(error, 'suggestCompetitors');
  }
};

/**
 * Analyze llms.txt files for given domains using AI
 * @param {string[]} domains - Array of domains to analyze
 * @param {string} organizationName - The name of the organization
 * @param {string} industry - The industry/sector of the organization (optional)
 * @returns {Promise<Object>} - Analysis results with scores and recommendations
 */
export const analyzeLlmsTxtAI = async (domains, organizationName, industry = '') => {
  const analyzeLlmsTxt = httpsCallable(functions, 'analyzeLlmsTxt', {
    timeout: 120000 // 2 minute timeout for fetching and analyzing
  });

  try {
    const result = await analyzeLlmsTxt({ domains, organizationName, industry });
    return result.data;
  } catch (error) {
    handleFunctionError(error, 'analyzeLlmsTxt');
  }
};
